// v2 chest — an iron-banded strongbox on a stone plinth: a dark oak coffer
// under a stepped domed lid, wrapped by two iron straps that run down both faces
// and over the top, iron corner brackets, and a hasp with a brass padlock on the
// front (+z, road side). Builds fast, so it carries no scaffolding — it visibly
// grows from plinth to coffer to banded lid. Ported 1:1 from age-of-mcp's
// v2/chest.ts, using the library's shared `foundationSlab` helper.
import * as THREE from "three";
import { defineModel, foundationSlab, type Part } from "../../lib";

const OAK      = "#6e4f2a";
const OAK_LID  = "#7d5c33";
const IRON     = "#3c4046";
const BRASS    = "#c9a23a";
const PLINTH_TOP = 0.07;

function plinth(): Part {
  return { geometry: new THREE.BoxGeometry(0.42, 0.07, 0.34), color: "#7e7a74",
    position: [0, 0.035, 0] };
}

function body(): Part {
  return { geometry: new THREE.BoxGeometry(0.34, 0.18, 0.26), color: OAK,
    position: [0, PLINTH_TOP + 0.09, 0] };
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.38, 0.04)];
  if (s === 1) {
    return [plinth(),
      { geometry: new THREE.BoxGeometry(0.34, 0.08, 0.26), color: OAK,
        position: [0, PLINTH_TOP + 0.04, 0] }];
  }
  if (s === 2) return [plinth(), body()];

  const parts: Part[] = [
    plinth(),
    body(),
    // Stepped domed lid.
    { geometry: new THREE.BoxGeometry(0.36, 0.06, 0.28), color: OAK_LID,
      position: [0, 0.28, 0] },
    { geometry: new THREE.BoxGeometry(0.30, 0.045, 0.21), color: OAK_LID,
      position: [0, 0.3325, 0] },
  ];
  // Two iron straps wrapping front, top and back.
  for (const x of [-0.10, 0.10]) {
    parts.push(
      { geometry: new THREE.BoxGeometry(0.05, 0.05, 0.30), color: IRON,
        position: [x, 0.345, 0] },
      { geometry: new THREE.BoxGeometry(0.05, 0.245, 0.02), color: IRON,
        position: [x, 0.1925, 0.135] },
      { geometry: new THREE.BoxGeometry(0.05, 0.245, 0.02), color: IRON,
        position: [x, 0.1925, -0.135] },
    );
  }
  // Iron corner brackets at the coffer's feet.
  for (const [x, z] of [[-0.165, -0.125], [0.165, -0.125], [-0.165, 0.125], [0.165, 0.125]] as const) {
    parts.push({ geometry: new THREE.BoxGeometry(0.035, 0.06, 0.035), color: IRON,
      position: [x, 0.10, z] });
  }
  parts.push(
    // Hasp and brass padlock on the road face.
    { geometry: new THREE.BoxGeometry(0.045, 0.10, 0.02), color: IRON,
      position: [0, 0.27, 0.14] },
    { geometry: new THREE.BoxGeometry(0.06, 0.07, 0.03), color: BRASS,
      position: [0, 0.20, 0.145] },
  );
  return parts;
}

export const chestV2 = defineModel({
  id: "chest-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Chest (v2)",
  tags: ["building","chest","low-poly","age-of-mcp","fable"],
  summary: "Low-poly chest.",
  description: "A sturdy wooden lockbox for storing a small amount of goods.",
  author: "age-of-mcp",
  footprint: 0.36,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
